import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../../../services/api";
import { ProfileField, ProfileFieldGrid, ProfilePanel, ProfileTableWrap } from "./ProfileSection";
import { formatCurrencyInr, formatProfileDate } from "../employeeProfileUtils";
import { ArrowUpRight, BadgeCheck, CalendarDays, CircleX, HandCoins, IndianRupee, Info, Settings2 } from "lucide-react";

const MANAGE_PATH = "/settings/allowances-benefits/advance-taken";

function ManageLink() {
  return (
    <p className="ep-manage-link">
      <Settings2 size={15} aria-hidden />
      <span>Managed in</span>
      <Link to={MANAGE_PATH}>
        Settings → Allowances &amp; Benefits → Advance Taken
        <ArrowUpRight size={14} aria-hidden />
      </Link>
    </p>
  );
}

function apiMessage(err, fallback) {
  const detail = err?.response?.data?.detail ?? err?.response?.data;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const text = detail.map((item) => item?.msg || item).filter(Boolean).join(" ");
    if (text) return text;
  }
  return fallback;
}

function approvalOf(row) {
  const explicit = String(row?.approval_status || "").toLowerCase();
  if (explicit === "pending" || explicit === "approved" || explicit === "rejected") return explicit;
  return String(row?.status || "").toLowerCase() === "pending" ? "pending" : "";
}

function displayStatus(row) {
  const status = String(row?.status || "").toLowerCase();
  const approval = approvalOf(row);
  if (status === "pending" && approval === "approved") return "Approved";
  if (status === "pending") return "Pending approval";
  if (approval === "rejected" || status === "cancelled") return "Rejected";
  if (status === "applied") return "Applied";
  if (status === "closed") return "Closed";
  return status || "—";
}

function displayStatusClass(row) {
  const label = displayStatus(row);
  if (label === "Approved" || label === "Applied" || label === "Closed") {
    return "ep-fuel-status ep-fuel-status--approved";
  }
  if (label === "Rejected") return "ep-fuel-status ep-fuel-status--rejected";
  return "ep-fuel-status ep-fuel-status--pending";
}

function impactText(row) {
  const status = String(row?.status || "").toLowerCase();
  const approval = approvalOf(row);
  if (status === "pending" && approval !== "approved") {
    return "Not deducted until this advance is approved.";
  }
  if (status === "pending" && approval === "approved") {
    return "Approved. Will be deducted in the applicable payroll.";
  }
  if (status === "applied") return "Deducted in payroll. This amount will not be deducted again.";
  if (status === "closed") return "Recovered in a finalized payroll.";
  if (approval === "rejected" || status === "cancelled") {
    return "Rejected / cancelled. This amount is not deducted in payroll.";
  }
  return "";
}

/**
 * People Hub — read-only advance taken summary.
 * Mutations live under Settings → Allowances & Benefits → Advance Taken.
 */
export default function AdvanceTakenSection({ employeeId }) {
  const [ready, setReady] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [eligible, setEligible] = useState(null);
  const [advances, setAdvances] = useState([]);
  const [error, setError] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const load = useCallback(async () => {
    if (!employeeId) return;
    setError("");
    try {
      const res = await API.get("/salary/v2/enterprise/advances", { params: { employee_id: employeeId } });
      setEnabled(Boolean(res.data?.enabled));
      setEligible(res.data?.eligible);
      setAdvances(Array.isArray(res.data?.advances) ? res.data.advances : []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        setEnabled(false);
        setAdvances([]);
        setError("");
      } else {
        setError(apiMessage(err, "Unable to load salary advance."));
      }
    } finally {
      setReady(true);
    }
  }, [employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  const pending = useMemo(
    () => advances.find((row) => String(row.status).toLowerCase() === "pending") || null,
    [advances]
  );
  const lastAdvance = useMemo(() => advances[0] || null, [advances]);
  const outstanding = useMemo(() => {
    let total = 0;
    for (const row of advances) {
      const status = String(row.status || "").toLowerCase();
      const approval = approvalOf(row);
      if (status === "pending" && approval === "approved") total += Number(row.amount) || 0;
    }
    return total;
  }, [advances]);

  if (!ready) return null;

  if (!enabled) {
    return (
      <div id="advance-taken">
        <ProfilePanel
          title="Advance Taken"
          subtitle="Salary advances recovered through payroll · read-only"
          icon={HandCoins}
          tone="indigo"
        >
          {error ? <p className="ep-fuel-error">{error}</p> : null}
          <p className="ep-inline-note ep-inline-note--warn">
            <Info size={16} aria-hidden />
            <span>Advance Taken is currently OFF for this organisation.</span>
          </p>
          <ManageLink />
        </ProfilePanel>
      </div>
    );
  }

  const isEligible = eligible == null ? null : Boolean(eligible);

  return (
    <div id="advance-taken" className="ep-advance">
      <ProfilePanel
        title="Advance Taken"
        subtitle="Salary advances recovered through payroll · read-only"
        icon={HandCoins}
        tone="indigo"
        actions={
          <button type="button" className="ep-btn ep-btn-ghost ep-btn-sm" onClick={() => setDrawerOpen(true)}>
            View Advance Details
          </button>
        }
      >
        <ProfileFieldGrid columns={3}>
          <ProfileField
            icon={isEligible === false ? CircleX : BadgeCheck}
            tone={isEligible === false ? "rose" : "green"}
            label="Eligible"
            value={isEligible == null ? "—" : isEligible ? "Yes" : "No"}
          />
          <ProfileField icon={IndianRupee} tone="violet" label="Outstanding" value={formatCurrencyInr(outstanding)} />
          <ProfileField
            icon={CalendarDays}
            tone="blue"
            label="Last Advance"
            value={
              lastAdvance
                ? `${formatCurrencyInr(lastAdvance.amount)} · ${formatProfileDate(lastAdvance.advance_date)}`
                : "—"
            }
          />
        </ProfileFieldGrid>

        {pending ? (
          <div className="ep-advance-current">
            <div className="ep-advance-amount-row">
              <div>
                <span className="ep-advance-kicker">Active advance</span>
                <span className={displayStatusClass(pending)}>{displayStatus(pending)}</span>
                <strong className="ep-advance-amount">{formatCurrencyInr(pending.amount)}</strong>
                <span className="ep-advance-caption">Outstanding / To be recovered</span>
              </div>
            </div>
            <ProfileFieldGrid columns={3}>
              <ProfileField icon={CalendarDays} tone="blue" label="Advance Date" value={formatProfileDate(pending.advance_date)} />
              <ProfileField icon={BadgeCheck} tone="amber" label="Status" value={displayStatus(pending)} />
              <ProfileField icon={Info} tone="slate" label="Payroll Impact" value={impactText(pending)} />
            </ProfileFieldGrid>
          </div>
        ) : (
          <p className="ep-inline-note">
            <Info size={16} aria-hidden />
            <span>No active salary advance for this employee.</span>
          </p>
        )}

        {error ? <p className="ep-fuel-error" role="alert">{error}</p> : null}

        <ManageLink />
      </ProfilePanel>

      {drawerOpen ? (
        <div className="ep-advance-modal" role="presentation">
          <button
            type="button"
            className="ep-advance-backdrop"
            aria-label="Close"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="ep-advance-dialog" style={{ maxWidth: 560 }}>
            <h3>Advance details</h3>
            <p>Read-only history for this employee.</p>
            {advances.length === 0 ? (
              <div className="ep-fuel-empty">
                <strong>No advances on record</strong>
              </div>
            ) : (
              <ProfileTableWrap>
                <table className="ep-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {advances.map((row) => (
                      <tr key={row.id}>
                        <td>{formatProfileDate(row.advance_date)}</td>
                        <td>{formatCurrencyInr(row.amount)}</td>
                        <td>
                          <span className={displayStatusClass(row)}>{displayStatus(row)}</span>
                        </td>
                        <td>{row.rejection_reason || row.note || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ProfileTableWrap>
            )}
            <div className="ep-advance-dialog-actions">
              <Link className="ep-btn ep-btn-secondary" to={MANAGE_PATH}>
                Manage in Settings
              </Link>
              <button type="button" className="ep-btn ep-btn-ghost" onClick={() => setDrawerOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
